// Runtime values shared between the page and its row components. They live here
// rather than in production-planning.tsx because the row components already
// import their TYPES from that file: types are erased at compile time so the
// cycle is harmless, but importing runtime values across it would depend on
// module-initialisation order. Same split as notes-helpers.ts.

// A row's products must add up to the plan's Horas. Hours are floats and the UI
// produces values like 2.5 / 5.5, so compare with a tolerance — the same epsilon
// the backend validation uses.
export const HOURS_EPSILON = 0.01;

// Automatic drawer allocations use the same half-hour increment exposed by the
// product and per-machine hour inputs. The click rounds upward so it does not
// leave a pedido fractionally short when the exact quotient falls between steps.
export const HOURS_INCREMENT = 0.5;

// Hours are persisted and edited at hundredth-hour precision. Keep this as a
// numeric state-boundary operation (rather than display-only toFixed calls), so
// allocation, validation, totals, and the input all use the same value.
export const normalizeHours = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    const rounded = Math.round((Math.max(0, value) + Number.EPSILON) * 100) / 100;
    return Object.is(rounded, -0) ? 0 : rounded;
};

export const roundHoursUpToIncrement = (value: number): number => {
    const normalized = normalizeHours(value);
    if (normalized <= 0) return 0;
    return normalizeHours(
        Math.ceil((normalized - Number.EPSILON) / HOURS_INCREMENT) *
            HOURS_INCREMENT,
    );
};

// Fallback when a plan predates the shift_hours column, and the default for a
// brand-new plan. 8 is what the header knob always defaulted to.
export const DEFAULT_SHIFT_HOURS = 8;

// How long a given row's turno actually is: its own override when set, otherwise
// the plan's. Null is the normal case — only a machine that does not run the
// whole turno (maintenance, a late start) carries its own number. Every hours
// comparison on both sides of the wire goes through this, so the row footer, the
// save pre-check and the backend rule can never disagree about the target.
export const effectiveShiftHours = (
    rowShiftHours: number | null,
    planShiftHours: number,
): number => (rowShiftHours === null ? planShiftHours : rowShiftHours);

// What share of the machine's historical kg/hr a planned product is expected to
// actually hit, as a PERCENTAGE. 80 is the default the planner asked for: the
// historical average is what the machine did on its good and bad days together,
// and planning the whole turno at it has been optimistic in practice.
export const DEFAULT_EFFICIENCY = 80;

// THE conversion from a historical rate to the rate a plan should be read at.
// One function because five places quote kilos for the same planned product —
// the product line, the collapsed row summary, the plan-wide totals the drawer
// allocates from, the picker's ranking, and the hours a drawer click assigns —
// and an efficiency applied in four of them is worse than one applied nowhere.
export const effectiveKgHr = (
    rateValue: number,
    efficiencyPercent: number,
): number => (rateValue * efficiencyPercent) / 100;

// The inventory fields a planned product reads. Declared structurally rather
// than imported from production-planning.tsx so this module stays free of that
// file's types (see the header note).
export interface StockEntry {
    kilos: number;
    groups: number;
    kilos_committed: number;
    groups_committed: number;
}

// What is still promisable: on hand minus what is already sold and awaiting
// delivery. Floored at 0 — a committed quantity above recorded stock is a data
// problem, and a negative "disponible" would only confuse the plan.
//
// Here rather than inline because the expanded product line and the collapsed
// row summary both quote it, and a floor applied in one of them is exactly the
// kind of drift that makes an open row and a closed row disagree.
export const availableKilosOf = (entry: StockEntry): number =>
    Math.max(0, entry.kilos - entry.kilos_committed);

export const availableGroupsOf = (entry: StockEntry): number =>
    Math.max(0, entry.groups - entry.groups_committed);

let localRowProductCounter = 0;

// Stable local key for React lists — a planned product has no id until saved.
export const nextRowProductKey = (): string =>
    `row-product-${localRowProductCounter++}`;
